import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SearchIcon from '@mui/icons-material/Search';
import DeleteConfirmation from '../../components/MultiDeleteConfirmation';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Row, Col, UncontrolledTooltip } from 'reactstrap';
import { IconButton } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { apiPost } from '../../utils/Api';
import Search from './Search';

const List = ({ data, onDelete, onSelect, onAdd, clearSearchedPersons, setProgramContactId, programId, makePrimary, fromContactGrid,
    onEdit, onSearch, onAddFromSearch, hideButtons, hideAddButton, hideSearchButton, title, searchResults }) => {
    const [deleteConfirmationState, setDeleteConfirmationState] = useState({
        display: false,
        toDelete: [],
    });
    const [deleteEnabled, setDeleteEnabled] = useState(false);
    const [newData, setNewData] = useState(data);
    const [showSearch, setShowSearch] = useState(false);
    const gridApiRef = useRef(null);

    useEffect(() => {
        setNewData(data || [])
        if (gridApiRef.current) {
            gridApiRef.current.sizeColumnsToFit();
        }
    }, [data]);

    const handleDelete = () => {
        onDelete(deleteConfirmationState.toDelete.map(x => x.programContactId));
        setDeleteConfirmationState({
            display: false,
            toDelete: [],
        });
    }

    const handleDeleteClick = (e) => {
        setDeleteConfirmationState({
            display: true,
            toDelete: deleteConfirmationState.toDelete
        });
    }

    const handleDeleteConfirmationToggle = () => setDeleteConfirmationState({
        display: !deleteConfirmationState.display,
        toDelete: deleteConfirmationState.toDelete
    });

    const handleSelect = (selectedItem) => {
        onSelect(selectedItem.row.personId);
    }

    const handleToggleSearch = () => {
        clearSearchedPersons();
        setShowSearch((prevState) => !prevState);
    };

    const handleSelectionChanged = (e) => {
        if (e.length > 0) {
            setDeleteConfirmationState({
                display: false,
                toDelete: data.filter(x => e.find(y => y === x.personId))
            });
            setDeleteEnabled(true);
        }
        else
            setDeleteEnabled(false);
    };

    const handleAddFromSearch = (selectedPersons) => {
        setShowSearch(false);
        onAddFromSearch(selectedPersons);
    };
    const editPointOfContact = (item) => {
        setProgramContactId(item.row.programContactId)
        fromContactGrid === true && onEdit(item.id)
    }

    return (
        <React.Fragment>
            {!hideButtons && (
                <Row className="mb-1">
                    {title && (
                        <Col>
                            <h3>{title}</h3>
                        </Col>
                    )}
                    <Col className="d-flex flex-row-reverse">
                        <Button id="deleteContactButton" disabled={!deleteEnabled} onClick={handleDeleteClick}>
                            <DeleteIcon />
                        </Button>

                        {!hideAddButton && (
                            <React.Fragment>
                                <Button id="addContactButton" onClick={onAdd} className="me-1">
                                    <AddIcon />
                                </Button>
                                <UncontrolledTooltip delay={{ show: 0, hide: 0 }} target="addContactButton">
                                    Add point of contact.
                                </UncontrolledTooltip>
                            </React.Fragment>
                        )}

                        {!hideSearchButton && (
                            <Button id="searchButton" onClick={handleToggleSearch} className="me-1">
                                <SearchIcon />
                            </Button>
                        )}
                        <UncontrolledTooltip delay={{ show: 0, hide: 0 }} target="searchButton">
                            Search.
                        </UncontrolledTooltip>

                        <UncontrolledTooltip delay={{ show: 0, hide: 0 }} target="deleteContactButton">
                            Delete points of contact.
                        </UncontrolledTooltip>
                    </Col>
                </Row>
            )}

            <Row className="mb-1">
                <Col>
                    <div style={{ height: '100%', width: '100%' }}>
                        <DataGrid
                            rows={newData?.map(x => { return { id: x.personId, ...x } }) || []}
                            onRowSelectionModelChange={handleSelectionChanged}
                            onRowDoubleClick={(item) => editPointOfContact(item)}
                            autoHeight={true}
                            rowHeight={35}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25 } },
                            }}
                            columns={[
                                {
                                    field: 'displayName',
                                    headerName: 'Contact Name',
                                    flex: 3,
                                    headerCheckboxSelection: true,
                                },
                                {
                                    field: 'personType',
                                    headerName: 'Type',
                                    flex: 3,
                                },
                                {
                                    field: 'email',
                                    headerName: 'Email',
                                    flex: 3,
                                },
                                {
                                    field: 'phone',
                                    headerName: 'Phone',
                                    flex: 3,
                                },
                                {
                                    field: 'canMakePrimary',
                                    headerName: 'Make Primary',
                                    flex: 1,
                                    headerCheckboxSelection: false,
                                    align: 'center',
                                    renderCell: (params) => (
                                        <IconButton
                                            onClick={() => makePrimary(params)}
                                            size="small"
                                            style={{ margin: '0 auto' }}
                                        >
                                            <StarIcon
                                                style={{ color: params.row.isPrimary ? 'springgreen' : 'black' }}
                                            />
                                        </IconButton>
                                    ),
                                },
                            ]}

                            sx={{
                                backgroundColor: 'white'
                            }}
                            getRowClassName={(params) => 'grid-row'}
                        />
                    </div>
                </Col>
            </Row>
            <DeleteConfirmation
                title={`Delete point(s) of contact`}
                onDelete={handleDelete}
                onToggle={handleDeleteConfirmationToggle}
                toDelete={deleteConfirmationState.toDelete.map(x => x.displayName)}
                isOpen={deleteConfirmationState.display}
            />
            <Search isOpen={showSearch} toggle={handleToggleSearch} onSearch={onSearch} data={searchResults} onAdd={handleAddFromSearch} />
        </React.Fragment>
    );
};

List.propTypes = {
    data: PropTypes.array,
    onDelete: PropTypes.func,
    onSelect: PropTypes.func,
    onAdd: PropTypes.func,
    clearSearchedPersons: PropTypes.func,
    onSelectionChanged: PropTypes.func,
    onAddFromSearch: PropTypes.func,
    hideButtons: PropTypes.bool,
    hideAddButton: PropTypes.bool,
    hideSearchButton: PropTypes.bool,
    title: PropTypes.string,
    searchResults: PropTypes.array,
};

export default List;